import { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';

// MUI Theme
import { Paper, TextField } from '@mui/material';

// Utils
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { parseError } from '../../../utils';

// Services
import { uploadUserGuide } from '../../../services/userGuide.service';

// Common Components
import CustomModal from '../../common/withModal';
export const UploadUserGuideModal = ({ setModalOpen, forceReloadTable }: { setModalOpen: (open: boolean) => void; forceReloadTable: () => void }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { notifyMessage } = useNotifyMessage();

  interface FormValues {
    name: string;
    file: File | null;
  }

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      file: null,
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().required('File name is required'),
      file: Yup.mixed<File>()
        .nullable()
        .required('PDF file is required')
        .test('fileType', 'Only PDF files are allowed', (value) => value && value.type === 'application/pdf'),
    }),
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onSubmit: async () => {},
  });

  const handleFileSet = async (file: File | null) => {
    await formik.setFieldValue('file', file);
    await formik.validateForm();
  };

  const handleSubmitForm = async (e?: React.FormEvent<HTMLFormElement>) => {
    e?.preventDefault();

    const { file, name } = formik.values;
    try {
      setIsSubmitting(true);
      if (!file) throw new Error('No file selected');
      if (!name) throw new Error('Name is required');

      // Create a new File object with the provided name
      const renamedFile = new File([file], `${name}.pdf`, { type: file.type });

      await uploadUserGuide(renamedFile);

      notifyMessage('User Guide uploaded successfully', 'success');
      formik.resetForm();
      setModalOpen(false);
      forceReloadTable();
    } catch (err) {
      notifyMessage(parseError(err), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <CustomModal
      title='Upload New User Guide'
      isOpen={true}
      isValid={formik.isValid}
      isLoading={isSubmitting}
      setIsOpen={setModalOpen}
      maxWidth='md'
      onConfirm={handleSubmitForm}
    >
      <Paper elevation={3}>
        <form onSubmit={handleSubmitForm} style={{ width: 'auto', height: 'auto', padding: 20 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {/* Name Field */}
            <TextField
              id='name'
              name='name'
              label='File Name'
              placeholder='E-ULD User Guide 1.6.3'
              value={formik.values.name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.name && Boolean(formik.errors.name)}
              helperText={formik.touched.name && formik.errors.name}
            />

            {/* File Picker */}
            <input
              id='file'
              type='file'
              name='file'
              accept='.pdf'
              onChange={(e) => handleFileSet(e.currentTarget.files ? e.currentTarget.files[0] : null)}
              style={{ padding: '8px', fontSize: '14px' }}
            />
            {formik.errors.file && formik.touched.file && <div style={{ color: 'red' }}>{formik.errors.file}</div>}
          </div>
        </form>
      </Paper>
    </CustomModal>
  );
};
